import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Wifi, WifiOff, ShieldCheck, Copy, Check, Send, Cloud, KeyRound, Loader2, Save,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value || ""); setCopied(true); toast.success("Copiado!"); setTimeout(() => setCopied(false), 1500); };
  return (
    <div>
      <label className="text-xs font-medium text-slate-500">{label}</label>
      <div className="flex gap-2 mt-1">
        <Input readOnly value={value || ""} className="font-mono text-xs bg-slate-50" data-testid={`${testid}-value`} />
        <Button type="button" variant="outline" size="icon" onClick={copy} data-testid={`${testid}-copy`}>
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export default function WhatsAppPage() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ access_token: "", phone_number_id: "", business_account_id: "" });
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/whatsapp/status").then((r) => {
    setStatus(r.data);
    setForm((f) => ({ ...f, phone_number_id: r.data.phone_number_id || "", business_account_id: r.data.business_account_id || "" }));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.phone_number_id.trim()) { toast.error("Informe o Phone Number ID"); return; }
    if (!status?.has_token && !form.access_token.trim()) { toast.error("Informe o Access Token"); return; }
    setSaving(true);
    try {
      await api.post("/whatsapp/config", form);
      toast.success("Credenciais salvas com segurança no servidor!");
      setForm((f) => ({ ...f, access_token: "" }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
    finally { setSaving(false); }
  };

  const testSend = async () => {
    if (!testTo.trim()) { toast.error("Informe um número de WhatsApp"); return; }
    setSending(true);
    try { await api.post("/whatsapp/test-send", { to: testTo }); toast.success("Mensagem de teste enviada! Verifique o WhatsApp de destino."); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar"); }
    finally { setSending(false); }
  };

  if (!status) return null;
  const connected = status.connected;
  const webhookUrl = `${BACKEND}${status.webhook_path || "/api/whatsapp/webhook"}`;

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl" data-testid="whatsapp-page">
      <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">WhatsApp</h1><p className="text-sm text-slate-500 mt-1">Envio pela API Oficial da Meta (Cloud API) — canal estável para produção.</p></div>

      {/* Status */}
      <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm text-center">
        <div className={`h-16 w-16 rounded-2xl mx-auto flex items-center justify-center ${connected ? "bg-emerald-100" : "bg-red-100"}`}>
          {connected ? <Wifi className="h-8 w-8 text-emerald-600" /> : <WifiOff className="h-8 w-8 text-red-500" />}
        </div>
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="whatsapp-status">
          <span className={`h-3 w-3 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
          <h2 className="font-display text-xl font-bold text-slate-900">{connected ? "API Oficial conectada" : (status.configured ? "Token expirado ou inválido" : "WhatsApp não configurado")}</h2>
        </div>
        <div className="inline-flex items-center gap-2 mt-3">
          {connected
            ? <span className="text-xs px-3 py-1 rounded-full bg-sky-100 text-sky-700 border border-sky-200 font-medium">API Oficial Meta Cloud</span>
            : <span className="text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-medium">{status.configured ? "AÇÃO NECESSÁRIA" : "MODO DEMONSTRAÇÃO"}</span>}
        </div>
      </div>

      {status.configured && status.token_valid === false && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex gap-3" data-testid="token-invalid-alert">
          <WifiOff className="h-5 w-5 shrink-0 mt-0.5 text-red-500" />
          <div>
            <p className="font-semibold">As mensagens não estão sendo enviadas: {status.token_error || "token inválido"}.</p>
            <p className="mt-1 text-red-700">O token de teste da Meta expira a cada 24h. Gere um <strong>token permanente</strong> (passo a passo abaixo) e cole no campo Access Token para voltar a enviar.</p>
          </div>
        </div>
      )}

      {/* Credentials form */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4" data-testid="whatsapp-config-form">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-sky-600" />
          <h3 className="font-semibold text-slate-800">Credenciais da Meta</h3>
          {status.has_token && status.phone_number_id
            ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">Configurada</span>
            : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">Pendente</span>}
        </div>
        <p className="text-xs text-slate-500">Cole aqui o Access Token e o Phone Number ID. Eles são salvos <strong>apenas no servidor</strong> — o token nunca é exibido de volta nem aparece no navegador.</p>
        <div className="grid gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Access Token {status.has_token && <span className="text-emerald-600">(já configurado — deixe em branco para manter)</span>}</label>
            <Input type="password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} placeholder={status.has_token ? "•••••••••• (configurado)" : "Cole seu Access Token"} className="mt-1" data-testid="cfg-access-token" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Phone Number ID</label>
            <Input value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })} placeholder="Ex: 123456789012345" className="mt-1" data-testid="cfg-phone-id" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">WhatsApp Business Account ID (opcional)</label>
            <Input value={form.business_account_id} onChange={(e) => setForm({ ...form, business_account_id: e.target.value })} placeholder="Opcional" className="mt-1" data-testid="cfg-waba-id" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600" data-testid="cfg-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salvar credenciais
        </Button>
        <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800 flex gap-2" data-testid="deploy-notice">
          <Cloud className="h-4 w-4 shrink-0 mt-0.5" />
          <span>As credenciais ficam salvas <strong>por ambiente</strong>. Ao publicar o app, abra o site publicado e salve as credenciais aqui novamente (uma única vez) para a API funcionar em produção.</span>
        </div>
      </div>

      {/* Permanent token guide */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-3" data-testid="permanent-token-guide">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /><h3 className="font-semibold text-slate-800">Token permanente (não expira em 24h)</h3></div>
        <p className="text-xs text-slate-500">O token de teste da Meta expira a cada 24h. Gere um <strong>Token de Usuário do Sistema</strong> permanente:</p>
        <ol className="space-y-1.5 text-sm text-slate-600 list-decimal list-inside">
          <li>Acesse <span className="font-mono text-xs bg-slate-100 px-1 rounded">business.facebook.com</span> → Configurações do Negócio.</li>
          <li>Em <strong>Usuários → Usuários do sistema</strong>, clique em <strong>Adicionar</strong> e crie um usuário com função <strong>Admin</strong>.</li>
          <li>Clique em <strong>Adicionar ativos</strong> → selecione seu app/WhatsApp e dê acesso total.</li>
          <li>Clique em <strong>Gerar novo token</strong> → escolha seu app → marque as permissões <span className="font-mono text-xs bg-slate-100 px-1 rounded">whatsapp_business_messaging</span> e <span className="font-mono text-xs bg-slate-100 px-1 rounded">whatsapp_business_management</span>.</li>
          <li>Em "Expiração do token", escolha <strong>Nunca</strong>. Copie o token e cole no campo Access Token acima.</li>
        </ol>
      </div>

      {/* Webhook */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4" data-testid="whatsapp-webhook-section">
        <div className="flex items-center gap-2"><Cloud className="h-5 w-5 text-sky-600" /><h3 className="font-semibold text-slate-800">Webhook (configurar na Meta)</h3></div>
        <CopyField label="URL do Webhook (Callback URL)" value={webhookUrl} testid="webhook-url" />
        <CopyField label="Verify Token (Token de Verificação)" value={status.verify_token} testid="verify-token" />
        <p className="text-xs text-slate-400">Em Meta for Developers → WhatsApp → Configuration: cole a Callback URL (sem barra no final) e o Verify Token, clique em "Verify and save" e assine o campo <strong>messages</strong>.</p>
      </div>

      {/* Test send */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-3" data-testid="whatsapp-test-section">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Send className="h-4 w-4 text-emerald-600" /> Testar envio</h3>
        <label className="text-xs font-medium text-slate-500">Número no formato internacional (ex: +5511999999999)</label>
        <div className="flex gap-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+55 11 99999-9999" data-testid="test-send-input" />
          <Button onClick={testSend} disabled={sending || !connected} className="bg-sky-600 hover:bg-sky-700 shrink-0" data-testid="test-send-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Enviar teste
          </Button>
        </div>
        {!connected && <p className="text-xs text-amber-600">Salve as credenciais da Meta para habilitar o envio real.</p>}
        <p className="text-xs text-slate-400">No modo de teste da Meta, o número precisa estar na lista de destinatários autorizados.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Segurança</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex gap-2"><span className="text-emerald-500">✓</span> O Access Token fica somente no servidor — nunca exposto no navegador.</li>
          <li className="flex gap-2"><span className="text-emerald-500">✓</span> Envio pela API oficial, sem QR Code (estável em produção).</li>
          <li className="flex gap-2"><span className="text-emerald-500">✓</span> Dados dos clientes protegidos conforme a LGPD.</li>
        </ul>
      </div>
    </div>
  );
}
